import { type Maybe } from '#app/__generated__/api.js';
import { getTypedKeys } from '#app/types/utils.js';
import { formatUrl } from '#app/utilities/formatUrl.js';
import { isValidEmail } from '#app/utilities/isValidEmail.js';
import { brands } from './constants.js';
import { type BrandDetails, type KnownBrand } from './types.js';

/**
 * Gets the root domain for any link.
 * https://contra.com/some-path -> contra.com
 * https://sub1.sub2.contra.com/some-path -> contra.com
 * @param link the link for which to get the domain
 * @returns the domain name if the link is valid otherwise returns null
 */
const getMaybeLinkDomain = (link: string): Maybe<string> => {
  try {
    // throws if the link is not valid
    const url = new URL(formatUrl(link));
    const hostnameParts = url.hostname.split('.');

    if (hostnameParts.length < 2) {
      // it is not a publicly accessible hostname
      return null;
    }

    return hostnameParts.slice(-2).join('.');
  } catch {
    return null;
  }
};

/**
 * @param domain the domain name for which to fetch the internal brand ID
 * @returns the brand ID if the domain matches one from our internal list. Returns null otherwise
 */
export const getMaybeBrandIdFromDomain = (
  domain: string,
): Maybe<KnownBrand> => {
  const brandIds = getTypedKeys(brands);

  const matchingId = brandIds.find((brandId) => {
    const brandDetails = brands[brandId];

    if (Array.isArray(brandDetails.domain)) {
      return brandDetails.domain.includes(domain.toLowerCase());
    }

    return brandDetails.domain === domain.toLowerCase();
  });

  if (typeof matchingId !== 'undefined') {
    return matchingId;
  }

  return null;
};

/**
 * Returns the brand details (name and domain) for a given link
 * @param link link for which details are to be obtained
 * @returns {BrandDetails} If the link is good. Returns null otherwise.
 */
export const getMaybeBrandDetailsFromLink = (
  link: string,
): Maybe<BrandDetails & { id?: KnownBrand }> => {
  const domain = getMaybeLinkDomain(link);

  if (domain === null) {
    return { domain: link, name: link };
  }

  const brandId = getMaybeBrandIdFromDomain(domain);

  if (brandId !== null) {
    return {
      ...brands[brandId],
      id: brandId,
    };
  }

  const emailCandidate = link.replace('mailto:', '');
  const isEmail = isValidEmail(emailCandidate);

  if (isEmail) {
    return { domain, name: emailCandidate };
  }

  return { domain, name: domain };
};

export const isKnownBrand = (nid: string): nid is KnownBrand => {
  return Object.prototype.hasOwnProperty.call(brands, nid);
};
